import type { JSONB } from '@risksmart-app/domain/src/types/common.types';
import type { InsertIngestionConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertIngestionConfigDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface InsertIngestionConfigInput {
  // The config blob consumers hold is a parsed JSON object (`JSON.parse` /
  // spread literal); the tRPC procedure input is the shared `JSONB` type. The
  // runtime values are identical, so the public input takes the loose shape and
  // we narrow to `JSONB` at the single mapInput boundary below.
  IngestionConfig?: Record<string, unknown> | null;
  // Write-only: stored in Secrets Manager server-side, never returned.
  ApiKey?: string | null;
}

export const useInsertIngestionConfig = createMutationHook({
  name: 'insertIngestionConfig',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.ingestionConfig.insert.mutationOptions({ onSuccess }),
  mapInput: (input: InsertIngestionConfigInput) => ({
    // Loose parsed-JSON object → shared JSONB; identical at runtime.
    IngestionConfig: (input.IngestionConfig ?? null) as JSONB | null,
    ApiKey: input.ApiKey ?? null,
  }),
  invalidate: (trpc) => [trpc.frontend.ingestionConfig.getAll.queryKey()],
  mapResponse: (result: { Id: string }): InsertIngestionConfigMutation => ({
    insertChildIngestionConfig: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertIngestionConfigDocument,
  graphqlVariables: (input: InsertIngestionConfigInput) => ({
    object: {
      IngestionConfig: input.IngestionConfig ?? undefined,
      ApiKey: input.ApiKey ?? undefined,
    },
  }),
  evictFields: ['ingestion_config'],
});
